<script setup lang="ts">
import { onMounted, ref, toRefs, computed } from "vue";
import { compareDesc, utc2local } from "@/utils";
import type { Order } from "@/rvapi";
import RvApi from "@/rvapi";

const props = defineProps<{
  searchId: string;
  type: string;
}>();
const { searchId, type } = toRefs(props);
const pageSize = 20;
const skipCount = ref(0);
const title = ref("Recent orders");
const orders = ref<Order[]>([]);
const state = ref("searching");
const lookupType = computed(() => {
  const map: Record<string, string> = {
    device: "history/deviceId",
    user: "history/userId",
  };
  return map[type.value] ?? "";
});

const addOrderIfNotAlreadyInArray = (order: Order) => {
  for (const existing of orders.value) {
    if (existing.orderId === order.orderId) {
      return;
    }
  }
  orders.value.push(order);
};

const mergeOrders = (newOrders: Order[]) => {
  for (const o of newOrders) {
    addOrderIfNotAlreadyInArray(o);
  }
  orders.value.sort((a, b) => compareDesc(a.orderDate, b.orderDate));
};

const nextPage = async (
  skip: number,
  limit: number,
): Promise<{ done: boolean; orders: Order[] }> => {
  const p1 = RvApi.searchGenericLookup<Order>(
    lookupType.value,
    searchId.value,
    limit,
    skip,
  );
  const p2 = RvApi.recentOrders(searchId.value, type.value, limit, skip);
  const o1 = await p1;
  const o2 = await p2;
  const done = o1.length != limit && o2.length != limit;

  return { done, orders: o2.concat(o1) };
};

const full = async () => {
  orders.value = [];
  skipCount.value = 0;
  title.value = "Order history";
  await more();
};
const more = async () => {
  state.value = "searching";
  const result = await nextPage(skipCount.value, pageSize);

  if (result.orders.length) {
    mergeOrders(result.orders);
  }
  skipCount.value += pageSize;

  if (result.done) {
    state.value = "alldone";
  } else {
    state.value = "extended";
  }
};
onMounted(async () => {
  const result = await nextPage(0, 5);

  if (result.orders.length) {
    mergeOrders(result.orders);
  }
  if (result.done) {
    state.value = "alldone";
  } else {
    state.value = "recent";
  }
});
</script>

<template>
  <h2>{{ title }}</h2>
  <pn-table bordered="true" striped="true">
    <table>
      <thead>
        <tr>
          <th>Order id</th>
          <th>Order date</th>
          <th>Customer email</th>
          <th>Device</th>
          <th>Status</th>
          <th>Payment method</th>
          <th>Platform</th>
          <th>Total amount</th>
          <th># Receivers</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="order in orders" :key="order.orderId">
          <td>
            <a :href="'/order/' + order.orderId">{{ order.orderId }}</a>
          </td>
          <td>{{ utc2local(order.orderDate) }}</td>
          <td>{{ order.customerEmail }}</td>
          <td>
            <RouterLink :to="'/device/' + order.deviceId">{{
              order.deviceId?.substring(0, 8) + "..."
            }}</RouterLink>
          </td>
          <td>{{ order.orderStatus }}</td>
          <td>{{ order.paymentMethod }}</td>
          <td>{{ order.platform }}</td>
          <td>{{ order.totalAmount / 100 }} {{ order.currencyCode }}</td>
          <td>{{ order.totalReceivers }}</td>
        </tr>
      </tbody>
    </table>
  </pn-table>
  <pn-spinner size="2" v-show="state === 'searching'" />
  <pn-button
    v-show="state === 'recent'"
    small="true"
    v-on:click="full"
    appearance="light"
  >
    Show full order history
  </pn-button>
  <pn-button
    v-show="state === 'extended'"
    small="true"
    v-on:click="more"
    appearance="light"
  >
    Load more
  </pn-button>
  <pn-toast
    closable="false"
    v-show="state === 'alldone'"
    text="All orders loaded"
  ></pn-toast>
</template>
